import { useMemo } from 'react';

import { type ApolloError, useQuery } from '@apollo/client';
import { EnterpriseContractPrice_PricingModel as pricingModel } from '__generated__/graphql-types';

import { get } from 'js/lib/user';

import { WES_DEFAULT_CURRENCY_CODE } from 'bundles/wes-common/constants/currency';
import EnterpriseSelfServeProductPrice from 'bundles/wes-common/queries/EnterpriseSelfServeProductPrice.graphql';
import type {
  EnterpriseSelfServeProductPriceQuery,
  EnterpriseSelfServeProductPriceQueryVariables,
} from 'bundles/wes-common/queries/__generated__/EnterpriseSelfServeProductPrice';
import type { ProductPrice } from 'bundles/wes-common/types';
import { generateImpressionForLocalisedPricing } from 'bundles/wes-common/utils/experiments';

type UseProductPriceParams = {
  learnerCount?: number;
  billingType?: number;
  enterprisePromotionId?: string;
  skipApiCall?: boolean;
  isDifferentialPricingEnabled?: boolean;
};

type UseProductPriceReturn = {
  productPrice: ProductPrice | null;
  defaultProductPrice: ProductPrice | null;
  loading: boolean;
  error?: ApolloError;
};

// Transform GraphQL data to match ProductPrice structure
function transformProductPriceData(
  data: EnterpriseSelfServeProductPriceQuery | undefined
): Array<ProductPrice> | undefined {
  return data?.EnterpriseContractPriceQueries?.getEnterpriseSelfServeContractProductPrice?.elements?.map(
    (element): ProductPrice => ({
      unitPrice: element.price.unitPrice.toString(),
      referenceUnitPrice: element.price.referenceUnitPrice?.toString() ?? null,
      rawLocalAmountTotal: element.price.rawLocalAmountTotal.toString(),
      discountPercent: element.price.discountPercent,
      countryIsoCode: element.price.countryIsoCode,
      actualLocalAmountDueToday: element.price.actualLocalAmountDueToday.toString(),
      actualLocalAmountTotal: element.price.actualLocalAmountTotal.toString(),
      rawLocalAmountDueToday: element.price.rawLocalAmountDueToday.toString(),
      actualLocalDiscountedAmount: element.price.actualLocalDiscountedAmount.toString(),
      rawAmountTotal: element.price.rawAmountTotal.toString(),
      rawAmountDueToday: element.price.rawAmountDueToday.toString(),
      currencyCode: element.price.currencyCode,
      id: `${element.id.userLimit}-${element.id.billingType}`, // Construct ID
    })
  );
}

export const useEnterpriseSelfServePrice = ({
  learnerCount,
  billingType,
  enterprisePromotionId,
  skipApiCall,
  isDifferentialPricingEnabled,
}: UseProductPriceParams): UseProductPriceReturn => {
  const userId = get()?.id ?? 0;

  const isLocalisedPricing = useMemo(() => {
    if (skipApiCall) return false;

    return generateImpressionForLocalisedPricing();
  }, [skipApiCall]);

  // GraphQL Query approach

  const {
    loading: gqlLoading,
    error: gqlError,
    data: graphqlData,
  } = useQuery<EnterpriseSelfServeProductPriceQuery, EnterpriseSelfServeProductPriceQueryVariables>(
    EnterpriseSelfServeProductPrice,
    {
      variables: {
        userLimit: learnerCount ?? 1,
        termInstallmentsTotal: billingType ?? 1,
        enterprisePromotionId,
        countryIsoCode: '',
        currencyCode: isLocalisedPricing ? null : WES_DEFAULT_CURRENCY_CODE,
        userId: userId.toString(),
        pricingModel: isDifferentialPricingEnabled ? pricingModel.DifferentialQuarterlyPremium : pricingModel.Standard,
      },
      context: { clientName: 'gatewayGql' },
      skip: skipApiCall,
    }
  );

  const transformedGraphQLData = useMemo(() => transformProductPriceData(graphqlData), [graphqlData]);

  // Return unified interface - both productPrice and defaultProductPrice use the same data
  // Backend has unnecessary pagination structure, so we just return the first element until they fix it
  return {
    productPrice: transformedGraphQLData?.[0] ?? null,
    defaultProductPrice: transformedGraphQLData?.[0] ?? null,
    loading: gqlLoading,
    error: gqlError,
  };
};
